import { API_BASE_URL, FREE_STORAGE_DAYS, TRANSIT_DAYS } from "@kupikupi/brand";
import { type FeaturedHomeCategory, type FeaturedHomeItem, useFeaturedHome, useStorageConfig, useTelegramLogin } from "@kupikupi/api";
import { convertFenToKopecks, formatPrice, useExchangeRate } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { CameraIcon } from "../components/CameraIcon";
import { CartHeaderButton } from "../components/CartHeaderButton";
import { type IconName, MiniIcon } from "../components/MiniIcon";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { setAuthToken, useAuth } from "../lib/auth";
import { useImageSearchPicker } from "../lib/image-search";
import { useProductUrlSearch } from "../lib/product-url-search";
import { getInviteCode, getRawInitData } from "../lib/telegram-auth";

const TILE_BG = [
  "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
  "linear-gradient(135deg,#E0F7F1,#B8E8DD)",
  "linear-gradient(135deg,#EFEBFF,#D7CCFF)",
  "linear-gradient(135deg,#FFE0E9,#FFC8D6)",
  "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
  "linear-gradient(135deg,#FFF0EB,#FFD3C2)",
] as const;

const BULLETS = [
  {
    titleKey: "welcome_b1_t",
    subKey: "welcome_b1_s",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  {
    titleKey: "welcome_b2_t",
    subKey: "welcome_b2_s",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  {
    titleKey: "welcome_b3_t",
    subKey: "welcome_b3_s",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
] as const;

const LOGIN_STEPS = ["login_step1", "login_step2", "login_step3"] as const;

export function IndexPage() {
  const { token: authToken, ready: authReady } = useAuth();
  const { t } = useTranslation("home");

  const navigate = useNavigate();
  const tgLogin = useTelegramLogin();
  const [loginStep, setLoginStep] = useState(-1); // -1 = idle, 0/1/2 = steps
  const [loginFailed, setLoginFailed] = useState(false);

  if (!authReady) {
    return (
      <TgPage>
        <div
          style={{
            minHeight: "100dvh",
            display: "grid",
            placeItems: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          …
        </div>
      </TgPage>
    );
  }

  if (authReady && !authToken) {
    const handleLoginClick = async () => {
      const initData = getRawInitData();
      if (!initData) return;

      setLoginFailed(false);
      setLoginStep(0);
      setTimeout(() => setLoginStep(1), 1400);

      try {
        const inviteCode = getInviteCode();
        const result = await tgLogin.mutateAsync(
          inviteCode ? { initData, inviteCode } : { initData },
        );
        if (result.xaccessToken) {
          setLoginStep(2);
          setTimeout(() => {
            setAuthToken(result.xaccessToken);
            navigate({ to: "/" as never });
          }, 800);
        }
      } catch {
        setLoginStep(-1);
        setLoginFailed(true);
      }
    };

    return (
      <TgPage>
        <div
          style={{
            minHeight: "100dvh",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            maxWidth: 480,
            margin: "0 auto",
            boxShadow: "0 0 0 1px var(--color-border)",
          }}
        >
          {/* header */}
          <div
            style={{
              background: "#fff",
              padding: "14px 18px",
              textAlign: "center",
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 15,
              letterSpacing: "-0.005em",
              color: "var(--color-text)",
              borderBottom: "1px solid var(--color-border)",
              flexShrink: 0,
            }}
          >
            kupikupi
          </div>

          {/* hero body */}
          <div
            style={{
              flex: 1,
              background:
                "radial-gradient(circle at 82% 12%, rgba(255,210,63,.18) 0%, transparent 42%), radial-gradient(circle at 12% 86%, rgba(255,107,74,.14) 0%, transparent 38%), linear-gradient(168deg, #087A63 0%, #0BA888 60%, #0F5B49 100%)",
              color: "#fff",
              padding: "32px 24px 28px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* dot pattern overlay */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle at 1.5px 1.5px, rgba(255,255,255,.07) 1.5px, transparent 0)",
                backgroundSize: "24px 24px",
                pointerEvents: "none",
              }}
            />

            {/* eyebrow */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,.9)",
                background: "rgba(255,255,255,.14)",
                padding: "5px 12px",
                borderRadius: 999,
                alignSelf: "center",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,.2)",
                marginBottom: 26,
                position: "relative",
                zIndex: 1,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--color-sticker-yellow)",
                  boxShadow: "0 0 8px var(--color-sticker-yellow)",
                }}
              />
              Telegram Mini App
            </span>

            {/* logo */}
            <div
              style={{
                width: 88,
                height: 88,
                background: "rgba(255,255,255,.14)",
                border: "1px solid rgba(255,255,255,.22)",
                borderRadius: 22,
                backdropFilter: "blur(14px)",
                margin: "0 auto 24px",
                display: "grid",
                placeItems: "center",
                padding: 12,
                boxShadow: "0 12px 28px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.18)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <img
                src="/brand/logo-mark.svg"
                alt="kupikupi"
                style={{ width: "100%", height: "100%", display: "block" }}
              />
            </div>

            {/* title */}
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: "-0.03em",
                lineHeight: 1.05,
                margin: "0 0 10px",
                textAlign: "center",
                textShadow: "0 2px 12px rgba(0,0,0,.15)",
                position: "relative",
                zIndex: 1,
              }}
            >
              {t("welcome_title_1", "登录即开始")}
              <br />
              <em style={{ fontStyle: "normal", color: "var(--color-sticker-yellow)" }}>
                {t("welcome_title_2", "中俄代购")}
              </em>
            </h1>

            {/* subtitle */}
            <p
              style={{
                fontSize: 13.5,
                color: "rgba(255,255,255,.92)",
                lineHeight: 1.6,
                margin: "0 auto 28px",
                textAlign: "center",
                maxWidth: 280,
                fontWeight: 500,
                textShadow: "0 1px 6px rgba(0,0,0,.15)",
                position: "relative",
                zIndex: 1,
              }}
            >
              {t("welcome_sub", "使用当前 Telegram 账户授权 kupikupi · 全程 5 秒 · 无需密码")}
            </p>

            {/* bullets */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 26,
                position: "relative",
                zIndex: 1,
              }}
            >
              {BULLETS.map((b) => (
                <div
                  key={b.titleKey}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr",
                    gap: 12,
                    background: "rgba(255,255,255,.11)",
                    border: "1px solid rgba(255,255,255,.18)",
                    borderRadius: 12,
                    padding: "12px 14px",
                    backdropFilter: "blur(8px)",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: "rgba(255,210,63,.22)",
                      color: "var(--color-sticker-yellow)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      border: "1px solid rgba(255,210,63,.3)",
                    }}
                  >
                    {b.icon}
                  </div>
                  <div>
                    <h4
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#fff",
                        letterSpacing: "-0.005em",
                        lineHeight: 1.3,
                        margin: "0 0 2px",
                      }}
                    >
                      {t(b.titleKey)}
                    </h4>
                    <p
                      style={{
                        fontSize: 11.5,
                        color: "rgba(255,255,255,.78)",
                        lineHeight: 1.5,
                        margin: 0,
                      }}
                    >
                      {t(b.subKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* footer */}
          <div
            style={{
              background: "#fff",
              padding: "20px 22px calc(20px + env(safe-area-inset-bottom))",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              borderTop: "1px solid var(--color-border)",
              flexShrink: 0,
            }}
          >
            {/* loading state */}
            {loginStep >= 0 && (
              <div
                style={{
                  margin: "-2px 0 8px",
                  background: "var(--color-surface)",
                  borderRadius: 12,
                  padding: "14px 16px",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {LOGIN_STEPS.map((stepKey, step) => {
                    const isDone = step < loginStep;
                    const isActive = step === loginStep;
                    return (
                      <div
                        key={step}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          fontSize: 12,
                          color: isDone || isActive ? "var(--color-text)" : "var(--color-text-muted)",
                          fontWeight: isActive ? 600 : 400,
                          transition: "color .3s",
                        }}
                      >
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: isDone ? "var(--color-primary-fill)" : "#fff",
                            color: isDone ? "#fff" : isActive ? "var(--color-primary-fill)" : "var(--color-text-muted)",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 800,
                            fontFamily: "var(--font-mono)",
                            border: isDone ? "1.5px solid var(--color-primary-fill)" : isActive ? "1.5px solid var(--color-primary-fill)" : "1.5px solid var(--color-border)",
                            position: "relative",
                          }}
                        >
                          {isDone ? "✓" : step + 1}
                          {isActive && (
                            <div
                              style={{
                                position: "absolute",
                                inset: -3,
                                border: "2px solid var(--color-primary-fill)",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                animation: "ml-spin .7s linear infinite",
                              }}
                            />
                          )}
                        </div>
                        <span>{t(stepKey)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TG button */}
            <button
              type="button"
              onClick={handleLoginClick}
              disabled={loginStep >= 0}
              style={{
                background: "linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)",
                color: "#fff",
                border: 0,
                padding: "16px 20px",
                borderRadius: 14,
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: 15,
                letterSpacing: "-0.005em",
                cursor: loginStep >= 0 ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                width: "100%",
                boxShadow: "0 8px 22px rgba(34,158,217,.32)",
                position: "relative",
                overflow: "hidden",
                opacity: loginStep >= 0 ? 0.6 : 1,
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  background: "#fff",
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <svg viewBox="0 0 24 24" fill="#229ED9" width={15} height={15}>
                  <path d="M22 3.5L2.5 11.5c-.8.3-.8 1.4 0 1.7l4.5 1.7 1.7 5.6c.2.7 1.1.9 1.6.4l2.5-2.4 4.7 3.4c.7.5 1.7.1 1.9-.8L23 4.6c.2-.8-.6-1.5-1.4-1.2zM9.6 14.8l-.7 4.1-1.3-4.5L18.3 7l-8.7 7.8z" />
                </svg>
              </span>
              <span>{t("login_tg", "通过 Telegram 授权登录")}</span>
              {/* shine animation */}
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: "-50%",
                  width: "50%",
                  height: "100%",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,.28), transparent)",
                  animation: "ml-shine 2.4s ease-in-out infinite",
                  pointerEvents: "none",
                }}
              />
            </button>

            {/* login error */}
            {loginFailed && (
              <p style={{ fontSize: 12, color: "var(--color-danger)", textAlign: "center", margin: "-4px 0 0" }}>
                登录失败 · 请重试
              </p>
            )}

            {/* meta hint */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                fontSize: 11,
                color: "var(--color-text-muted)",
                marginTop: -4,
              }}
            >
              <span>{t("login_meta_target", "授权目标")}</span>
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: "var(--color-border-strong)",
                }}
              />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  background: "var(--color-surface)",
                  padding: "2px 7px",
                  borderRadius: 5,
                  fontWeight: 800,
                  color: "var(--color-text)",
                  letterSpacing: "0.02em",
                }}
              >
                {t("login_meta_bot", "@kupikupi_bot")}
              </span>
            </div>

            {/* legal */}
            <p
              style={{
                fontSize: 10.5,
                color: "var(--color-text-muted)",
                lineHeight: 1.55,
                textAlign: "center",
                margin: 0,
              }}
            >
              <Trans
                i18nKey="login_foot"
                ns="home"
                components={{
                  0: <a href="/terms" style={{ color: "var(--color-primary-fill)", textDecoration: "none", fontWeight: 700 }} />,
                  1: <a href="/privacy" style={{ color: "var(--color-primary-fill)", textDecoration: "none", fontWeight: 700 }} />,
                  2: <strong style={{ color: "var(--color-text)" }} />,
                }}
              />
            </p>
          </div>
        </div>
      </TgPage>
    );
  }

  return <HomeLoggedIn />;
}

// ── Shimmer tile helper ────────────────────────────────────────────────
const SHIMMER_STYLE: React.CSSProperties = {
  background: "linear-gradient(90deg, var(--color-surface) 25%, var(--color-border) 50%, var(--color-surface) 75%)",
  backgroundSize: "400% 100%",
  animation: "tg-shimmer 1.4s ease-in-out infinite",
  borderRadius: 8,
};

// ── Curated mini section ────────────────────────────────────────────────
function CuratedMiniSection({ token }: { token: string | null }) {
  const { t } = useTranslation("home");
  const { data: categories = [], isLoading, isError } = useFeaturedHome({ authScope: token });
  const [activeId, setActiveId] = useState<string>("");
  // Live CNY→RUB rate; same query key as cart/warehouse so TanStack deduplicates.
  const { data: rate } = useExchangeRate(API_BASE_URL);
  const rubPerYuan = rate?.rubPerYuan ?? null;

  const visibleCats = categories.filter((c: FeaturedHomeCategory) => c.items.length > 0);
  const resolvedId = visibleCats.some((c: FeaturedHomeCategory) => c.id === activeId)
    ? activeId
    : (visibleCats[0]?.id ?? "");
  const activeCat = visibleCats.find((c: FeaturedHomeCategory) => c.id === resolvedId) ?? visibleCats[0];
  const records: FeaturedHomeItem[] = activeCat?.items ?? [];
  const total = visibleCats.reduce((n: number, c: FeaturedHomeCategory) => n + c.items.length, 0);

  if (!isLoading && !isError && total === 0) return null;

  return (
    <section className="e-curated e-curated--mini" style={{ padding: "0", background: "transparent" }}>
      <div className="e-curated__inner" style={{ padding: "0" }}>
        <header className="e-curated__head" style={{ marginBottom: 10, alignItems: "center" }}>
          <div>
            <h2 className="e-curated__t">
              {t("curated.title").replace("<accent>", "").replace("</accent>", "")}
            </h2>
          </div>
          <Link
            to={"/catalog" as never}
            style={{ color: "var(--color-primary-fill)", fontSize: 11, fontWeight: 700, textDecoration: "none" }}
          >
            {t("curated.view_more")} →
          </Link>
        </header>

        {/* Tabs */}
        <div className="e-curated__tabs" role="tablist">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...SHIMMER_STYLE,
                    width: 72,
                    height: 30,
                    borderRadius: 999,
                    flexShrink: 0,
                  }}
                />
              ))
            : visibleCats.map((cat: FeaturedHomeCategory, i: number) => {
                const isActive = resolvedId === cat.id;
                const cls = [
                  "e-curated__tab",
                  i === 0 ? "e-curated__tab--first" : "",
                  isActive ? "is-active" : "",
                ].filter(Boolean).join(" ");
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveId(cat.id)}
                    className={cls}
                  >
                    {cat.name}
                  </button>
                );
              })}
        </div>

        {/* Grid */}
        {isLoading && (
          <div className="e-curated__grid" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  padding: 6,
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                }}
              >
                <div style={{ ...SHIMMER_STYLE, aspectRatio: "1", borderRadius: 8 }} />
                <div style={{ ...SHIMMER_STYLE, height: 12, borderRadius: 4, width: "90%" }} />
                <div style={{ ...SHIMMER_STYLE, height: 14, borderRadius: 4, width: "50%" }} />
              </div>
            ))}
          </div>
        )}

        {isError && (
          <p style={{ fontSize: 12, color: "var(--color-text-muted)", textAlign: "center", padding: "16px 0" }}>
            {t("curated.error")}
          </p>
        )}

        {!isLoading && !isError && records.length > 0 && (
          <>
            <div className="e-curated__grid">
              {records.map((item: FeaturedHomeItem, i: number) => {
                const key = item.productId || item.sourceProductKey || null;
                const title = item.title || item.id;
                const cardContent = (
                  <>
                    <div
                      style={{
                        aspectRatio: "1",
                        background: TILE_BG[i % TILE_BG.length],
                        borderRadius: 8,
                        display: "grid",
                        placeItems: "center",
                        fontSize: 36,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={title}
                          loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <span
                          style={{
                            color: "var(--color-text-muted)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <MiniIcon name="catalog" size={28} />
                        </span>
                      )}
                      {item.channel && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: 4,
                            left: 4,
                            background: "rgba(15,31,26,.7)",
                            color: "#fff",
                            fontSize: 8,
                            fontWeight: 700,
                            padding: "1px 5px",
                            borderRadius: 3,
                          }}
                        >
                          {item.channel}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {title}
                    </div>
                    {item.priceCnyFen != null && rubPerYuan != null && (
                      <div
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: 13,
                          fontWeight: 900,
                          color: "var(--color-price)",
                        }}
                      >
                        {formatPrice(convertFenToKopecks(item.priceCnyFen, rubPerYuan), "ru-RU", "RUB")}
                      </div>
                    )}
                  </>
                );

                const cardStyle: React.CSSProperties = {
                  background: "#fff",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  padding: 6,
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  textDecoration: "none",
                  color: "inherit",
                };

                if (item.channel && key) {
                  return (
                    <Link
                      key={item.id}
                      to={"/product/$channel/$spuNo" as never}
                      params={{ channel: item.channel, spuNo: key } as never}
                      style={cardStyle}
                    >
                      {cardContent}
                    </Link>
                  );
                }
                if (item.pdpUrl) {
                  return (
                    <a key={item.id} href={item.pdpUrl} target="_blank" rel="noopener noreferrer" style={cardStyle}>
                      {cardContent}
                    </a>
                  );
                }
                return (
                  <div key={item.id} style={cardStyle}>
                    {cardContent}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── Main logged-in home view ────────────────────────────────────────────
function HomeLoggedIn() {
  const { t } = useTranslation("home");
  const { token } = useAuth();
  const navigate = useNavigate();
  const imgSearch = useImageSearchPicker();
  const [homeKw, setHomeKw] = useState("");
  // Paste-a-link search (web parity #160): a pasted source-platform product URL
  // resolves straight to its PDP; anything else runs the existing keyword search.
  const { submit: submitUrlSearch, isResolving } = useProductUrlSearch();
  const [resolveFailed, setResolveFailed] = useState(false);
  const submitHomeSearch = () => {
    setResolveFailed(false);
    void submitUrlSearch(homeKw, {
      onKeywordSearch: (q) => {
        navigate({ to: "/catalog" as never, search: (q ? { keyword: q } : {}) as never });
      },
      onResolveFailed: () => setResolveFailed(true),
    });
  };
  // #889 — 仓储天数优先取后端真实值；env 常量仅作 fallback（同 tariffs.tsx #767）。
  const { data: storageConfig } = useStorageConfig();
  const freeDays = storageConfig?.freePeriodDays ?? FREE_STORAGE_DAYS;

  return (
    <TgPage>
      <TgHeader
        title="kupikupi"
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CartHeaderButton size={20} />
            <MiniIcon name="bell" size={20} />
          </div>
        }
        borderless
      />
      <div className="tg-hero">
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            background: "rgba(255,255,255,.7)",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: "var(--color-primary-fill)",
            marginBottom: 8,
          }}
        >
          {t("hero.eyebrow", { days: TRANSIT_DAYS })}
        </span>
        <h1>
          {t("hero.title_line1")}
          <br />
          {t("hero.title_line2").includes("<accent>") ? (
            <>
              {t("hero.title_line2").split("<accent>")[0]}
              <span className="accent">
                {t("hero.title_line2").split("<accent>")[1]?.split("</accent>")[0]}
              </span>
              {t("hero.title_line2").split("</accent>")[1] ?? ""}
            </>
          ) : (
            t("hero.title_line2")
          )}
        </h1>
        <p>{t("hero.subtitle")}</p>
        {/* #889 — 5 trust 徽章（安全支付/1:1质检/24/7客服/仓储/配送保障） */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: 14,
          }}
        >
          {([
            { key: "trust_safe_pay" },
            { key: "trust_qc_11" },
            { key: "trust_support_247" },
            { key: "trust_storage", days: freeDays },
            { key: "trust_delivery" },
          ] as Array<{ key: string; days?: number }>).map(({ key, days }) => (
            <span
              key={key}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                background: "rgba(255,255,255,.7)",
                border: "1px solid rgba(255,255,255,.8)",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                color: "var(--color-text)",
                backdropFilter: "blur(6px)",
              }}
            >
              <span style={{ color: "var(--color-primary-fill)", fontWeight: 900 }}>✓</span>
              {t(`hero.${key}`, { days: days ?? freeDays })}
            </span>
          ))}
        </div>
        {/* Editable search — type a keyword and submit goes straight to catalog
            results (no empty intermediate search page). */}
        <form
          className="tg-search"
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          onSubmit={(e) => {
            e.preventDefault();
            submitHomeSearch();
          }}
        >
          <span style={{ color: "var(--color-text-muted)", flexShrink: 0, display: "flex" }}>
            <MiniIcon name="search" size={18} />
          </span>
          <input
            type="search"
            value={homeKw}
            onChange={(e) => setHomeKw(e.target.value)}
            placeholder={t("hero.search_placeholder")}
            enterKeyHint="search"
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 15,
              color: "var(--color-text)",
            }}
          />
          <button
            type="button"
            onClick={() => imgSearch.open()}
            disabled={imgSearch.isUploading}
            title={t("hero.search_cam_title")}
            style={{
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: imgSearch.isUploading ? "var(--color-text-muted)" : "var(--color-primary-fill)",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              opacity: imgSearch.isUploading ? 0.5 : 1,
            }}
          >
            <CameraIcon size={18} />
          </button>
          <button type="submit" disabled={isResolving} style={{ flexShrink: 0 }}>
            {isResolving ? t("hero.search_resolving") : t("hero.search_button")}
          </button>
        </form>
        {/* Pasted product link the backend couldn't resolve to a PDP (#160) */}
        {resolveFailed && (
          <p
            style={{
              fontSize: 12,
              color: "var(--color-danger)",
              lineHeight: 1.45,
              margin: "8px 2px 0",
            }}
          >
            {t("hero.search_resolve_failed")}
          </p>
        )}
      </div>

      <div className="tg-body" style={{ paddingTop: 0, flex: 1 }}>
        {/* ⓪ Core service actions — the 4 things a reverse-海淘 user actually does
            (link submission = hero search above; here: estimate / orders / parcels /
            support). Gives a clear "what do I do next" line, not just product browsing. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 6,
            marginTop: "var(--space-stack-md)",
          }}
        >
          <HomeAction icon="calculator" label={t("action_estimate")} to="/estimation" />
          <HomeAction icon="orders" label={t("action_orders")} to="/orders" />
          <HomeAction icon="shipment" label={t("action_parcels")} to="/waybills" />
          <HomeAction icon="support" label={t("action_support")} href="https://t.me/kupikupi_support" />
        </div>

        {/* ① 黄色邀好友 sticker 横幅 — between hero and promo card */}
        <Link
          to={"/affiliate" as never}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "var(--color-sticker-yellow-soft)",
            border: "1px solid var(--color-sticker-yellow)",
            borderRadius: 10,
            padding: "8px 12px",
            fontSize: 11,
            color: "var(--color-sticker-yellow-fg)",
            textDecoration: "none",
            marginTop: "var(--space-stack-md)",
          }}
        >
          <span style={{ display: "flex", flexShrink: 0, color: "var(--color-sticker-yellow-fg)" }}>
            <MiniIcon name="coupon" size={16} />
          </span>
          <span>
            <Trans
              i18nKey="invite_strip"
              ns="home"
              components={{ strong: <strong /> }}
            />
          </span>
        </Link>

        <Link
          to={"/affiliate" as never}
          className="tg-promo"
          style={{ marginTop: "var(--space-stack-md)" }}
        >
          <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <h3 className="tg-promo__t">
              {t("promo.title")}
              <br />
              {t("promo.subtitle")}
            </h3>
            <p className="tg-promo__s">{t("promo.desc")}</p>
            <span className="tg-promo__cta">{t("promo.cta")}</span>
          </div>
          <div className="tg-promo__bear">🐻</div>
        </Link>

        <div className="tg-sec-h">
          <span className="tg-sec-h__t">{t("steps.title")}</span>
          <Link
            to={"/beginner-guide" as never}
            className="tg-sec-h__r"
            style={{ textDecoration: "none" }}
          >
            {t("steps.video_cta")}
          </Link>
        </div>
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "12px 14px",
            border: "1px solid var(--color-border)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 4,
              alignItems: "center",
            }}
          >
            {(["link", "topup", "catalog", "camera", "plane", "home"] as const).map((ic) => (
              <div
                key={ic}
                style={{ display: "flex", justifyContent: "center", color: "var(--color-primary-fill)" }}
              >
                <MiniIcon name={ic} size={18} />
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 4,
              fontSize: 9,
              color: "var(--color-text-muted)",
              textAlign: "center",
              marginTop: 4,
              fontWeight: 700,
            }}
          >
            {t("steps.labels")
              .split(",")
              .map((l: string) => (
                <span key={l}>{l}</span>
              ))}
          </div>
        </div>

        {/* ② 精选 Tab 区块 */}
        <CuratedMiniSection token={token} />
      </div>
      <TgBottomNav />
    </TgPage>
  );
}

/** Home core-action tile — icon + label, links internally (to) or external (href). */
function HomeAction({
  icon,
  label,
  to,
  href,
}: {
  icon: IconName;
  label: string;
  to?: string;
  href?: string;
}) {
  const style = {
    background: "#fff",
    border: "1px solid var(--color-border)",
    borderRadius: 10,
    padding: "12px 4px",
    textDecoration: "none",
    color: "inherit",
    display: "block",
    textAlign: "center" as const,
  };
  const inner = (
    <>
      <span style={{ color: "var(--color-primary-fill)", display: "flex", justifyContent: "center" }}>
        <MiniIcon name={icon} size={24} />
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, marginTop: 4, display: "block" }}>{label}</span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={style}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to as never} style={style}>
      {inner}
    </Link>
  );
}
